import React, { useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import layiMark from './assets/layi-mark-on-dark.svg'
import LayiFrame, { layiPalette } from './LayiFrame'
import { LAYI_FRAME_SRC, readFramePathname } from '../../chatbot/useLayiFrame'
import { surfaces } from '../../theme'

// The chat panel, docked by the widget at the right edge: a thin frame around the embedded LAYI
// (the same Chainlit UI the chat page shows full-size), with a header to open that page or
// collapse. The conversation itself — messages, settings, history — is Chainlit's, inside the
// iframe. The header pops the chat out to its own window or collapses the panel.
//
// The panel is a dark surface by design (the brand look), so it declares `color-scheme: dark`.
// The on-dark mark is used because the surface is brand navy — the default mark's navy diamonds
// would vanish on it.

const panelTheme = {
  // Same navy as the top bar and LAYI's dark background, so the panel reads as part of the chrome.
  surface: '#061e2d',
  border: 'rgba(23, 236, 178, 0.16)',
  surfaceRaised: 'rgba(255, 255, 255, 0.07)',
  hairlineCyan: 'rgba(24, 228, 237, 0.35)'
}

// Opening slides the panel in from the right edge it is docked to.
const panelIn = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to { opacity: 1; transform: translateX(0); }
`

// The panel fills the slot the widget gives it: square corners, the mint contour on its free left
// edge and a shadow cast on the page, like the panels docked at the left.
const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${panelTheme.surface};
  color: ${layiPalette.text};
  color-scheme: dark;
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  box-shadow: ${surfaces.dockedRightPanelShadow};
  overflow: hidden;
  animation: ${panelIn} 0.18s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid ${panelTheme.border};
  background: rgba(255, 255, 255, 0.03);
`

const Avatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: contain;
  box-sizing: border-box;
  padding: 2px;
  background: ${panelTheme.surfaceRaised};
  border: 1px solid ${panelTheme.hairlineCyan};
`

// 500 is a loaded Raleway face; 600 is not (100/300/400/500/700) and would silently round up to 700.
const Title = styled.span`
  flex: 1;
  font-weight: 500;
  font-size: 15px;
  letter-spacing: 2.5px;
`

const headerControl = `
  position: relative;
  background: none;
  border: none;
  color: ${layiPalette.textMuted};
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 6px;
  text-decoration: none;
  transition: color 0.2s ease, background 0.2s ease;

  &:hover {
    color: ${layiPalette.text};
    background: ${panelTheme.surfaceRaised};
  }

  /* Instant, high-contrast tooltip (the native title one is easy to miss). Anchored to the right
     edge so it never overflows the panel from these right-side header controls. */
  &::after {
    content: attr(data-tip);
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    background: ${layiPalette.text};
    color: ${layiPalette.surface};
    padding: 4px 8px;
    border-radius: 6px;
    font-family: 'Raleway', sans-serif;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
    opacity: 0;
    transform: translateY(-2px);
    transition: opacity 0.12s ease, transform 0.12s ease;
    pointer-events: none;
    z-index: 1;
  }

  &:hover::after,
  &:focus-visible::after {
    opacity: 1;
    transform: translateY(0);
  }
`

const HeaderButton = styled.button`
  ${headerControl}
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
`

// The pop-out continues the conversation the panel shows: the frame is same-origin, so its location
// says which LAYI thread is open (/layi/thread/<id>); anything else opens a fresh chat.
const popOutTarget = frame => {
  const pathname = readFramePathname(frame)
  return pathname && pathname.includes('/thread/') ? pathname : LAYI_FRAME_SRC
}

export default function ChatPanel({ locale, t, onClose, onPopOut }) {
  const bodyRef = useRef(null)
  const popOut = () => onPopOut(popOutTarget(bodyRef.current && bodyRef.current.querySelector('iframe')))

  return (
    <Panel>
      <Header>
        <Avatar src={layiMark} alt="" />
        <Title>LAYI</Title>
        <HeaderButton type="button" onClick={popOut} data-tip={t('layi_advanced_chat')} aria-label={t('layi_advanced_chat')}>
          ⧉
        </HeaderButton>
        <HeaderButton type="button" onClick={onClose} data-tip={t('layi_collapse')} aria-label={t('layi_collapse')}>
          ✕
        </HeaderButton>
      </Header>

      <Body innerRef={node => (bodyRef.current = node)}>
        <LayiFrame locale={locale} t={t} />
      </Body>
    </Panel>
  )
}
